/**
 * Drop-in replacement for sonner's `toast` that localizes server messages.
 *
 * API errors reach the UI as the raw `error` field of the response. The server
 * emits either a plain string (legacy) or an `__i18n__:<key>[:<params>]`
 * sentinel; a raw string used to be printed verbatim, which is how English
 * messages like "The score must be a number between 0 and the criterion's
 * maximum." showed up in a Catalan UI. Resolving here covers every toast in the
 * app — including the ones written in the future — instead of patching each
 * call site.
 *
 * Import from "@/lib/toast" instead of "sonner".
 */
import { toast as sonnerToast } from "sonner"
import i18n from "i18next"
import { resolveI18nString } from "./locale-format"

function localize(message: unknown): unknown {
	if (typeof message !== "string") return message
	return resolveI18nString(message, (key, params) => String(i18n.t(key, params as never)))
}

/**
 * Wrap a sonner variant so its first argument is localized; signature untouched.
 * `options.description` is localized too: it is where every call site puts the
 * server's error message, so leaving it raw printed the sentinel verbatim.
 */
function wrap<T extends (...args: never[]) => unknown>(fn: T): T {
	return ((...args: unknown[]) => {
		const [message, options, ...rest] = args
		const localizedOptions = options && typeof options === "object" && "description" in options
			? { ...(options as Record<string, unknown>), description: localize((options as { description?: unknown }).description) }
			: options
		return (fn as unknown as (...a: unknown[]) => unknown)(localize(message), localizedOptions, ...rest)
	}) as unknown as T
}

export const toast = Object.assign(wrap(sonnerToast), sonnerToast, {
	error: wrap(sonnerToast.error),
	success: wrap(sonnerToast.success),
	info: wrap(sonnerToast.info),
	warning: wrap(sonnerToast.warning),
	message: wrap(sonnerToast.message),
	loading: wrap(sonnerToast.loading),
})
